import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import moment from 'moment';
import { IStore } from 'redux/store';
import { inspectorSelectors } from 'redux/app/vessel/inspector';
import { vesselSelectors } from 'redux/app/vessel';

type OwnProps = {
    // isPrintVisible: boolean;
	vesselId: string;
};
type ORB1CoverPageProps = OwnProps & PropsFromRedux;

function IntroductionPage() {
    return (
        <div className="newpage flex flex-col justify-between py-0 pt-4 px-8 mb-2 text-justify">
            <h2 className="text-3xl font-medium">Introduction</h2>
            <p className="py-2">
                The following pages of this section show a comprehensive list of
                items of machinery space operations which are, when appropriate,
                to be recorded in the Oil Record Book Part I in accordance with
                regulation 17 of Annex I of the International Convention for the
                Prevention of Pollution from Ships, 1973, as modified by the
                Protocol of 1978 relating thereto (MARPOL 73/78). The items have
                been grouped into operational sections, each of which is denoted
                by a letter Code.
            </p>
            <p className="py-2">
                When making entries in the Oil Record Book Part I, the date,
                operational Code and item number shall be inserted in the
                appropriate Columns and the required particulars shall be
                recorded chronologically in the blank spaces. Each completed
                operation shall be signed for and dated by the officer or
                officers in charge. The master of the Ship shall sign each
                completed page.
            </p>
            <p className="py-2">
                The Oil Record Book Part I contains many references to oil
                quantity. The limited accuracy of tank Measurement devices,
                temperature variations and clingage will affect the accuracy of
                these readings. The entries in the Oil Record Book Part I should
                be considered accordingly.
            </p>
            <p className="py-2">
                In the event of accidental or other exceptional discharge of oil
                statement shall be made in the Oil Record Book Part I of the
                circumstances of, and the reasons for, the discharge.
            </p>
            <p className="py-2">
                Any failure of the oil filtering equipment shall be noted in the
                Oil Record Book Part I.
            </p>
            <p className="py-2">
                The entries in the Oil Record Book Part I, for ships holding an
                IOPP Certificate, shall be atleast in English, French or
                Spanish. Where entries in official language of the State whose
                flag the ship is entitled to fly are also used, this shall
                prevail in case of a dispute or discrepancy.
            </p>
            <p className="py-2">
                The Oil Record Book Part I shall be kept in such a place as to
                be readily available for inspection at all reasonable times and,
                except in the case of unmanned ships under tow, shall be kept on
                board the ship. It shall be preserved for a period of three
                years after the last entry has been made.
            </p>
            <p className="py-2">
                The competent authority of the Government of a Party to the
                Convention may inspect the Oil Record Book Part I on board any
                ship to which this Annex applies while the ship is in its port
                or offshore terminals and may make a copy of any entry in that
                book and may require the master of the ship to certify that the
                copy is a true copy of such entry. Any copy so made which has
                been certified by the master of the ship as a true copy of an
                entry in the Oil Record Book Part I shall be made admissible in
                any juridical proceedings as evidence of the facts stated in the
                entry. The inspection of an Oil Record Book Part I and the
                taking of a certified copy by the competent authority under this
                paragraph shall be performed as expeditiously as possible
                without causing the ship to be unduly delayed.
            </p>
        </div>
    );
}

function ItemsTobeRecorded() {
    return (
        <div className="newpage py-0 px-8">
            <h3 className="mb-6 font-semibold text-xl">
                LIST OF ITEMS TO BE RECORDED
            </h3>
            <ol type="A" className="list-alpha ml-4">
                <li className="mb-8">
                    <b>Ballasting or cleaning of oil fuel tanks</b>
                    <ol className="list-decimal ml-4">
                        <li className="my-2 mx-0">
                            Identity of tank(s) ballasted.
                        </li>
                        <li className="my-2 mx-0">
                            Whether cleaned since they last contained oil and,
                            if not, type of oil previously carried.
                        </li>
                        <li className="my-2 mx-0">
                            Cleaning process:
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    position of ship and time at the start and
                                    completion of cleaning;
                                </li>
                                <li className="my-2 mx-0">
                                    identify tank(s) in which one or another
                                    method has been employed (rinsing through,
                                    steaming, cleaning with chemicals; type and
                                    quantity of chemicals used, in m<sup>3</sup>
                                    );.
                                </li>
                                <li className="my-2 mx-0">
                                    identity of tank(s) into which cleaning
                                    water was transferred and the quantity in m
                                    <sup>3</sup>
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            Ballasting:
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    position of ship and time at start and end
                                    of ballasting;
                                </li>
                                <li className="my-2 mx-0">
                                    quantity of ballast if tanks are not
                                    cleaned, in m<sup>3</sup>
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>
                        Discharge of dirty ballast or cleaning water from oil
                        fuel tanks referred to under Section (A)
                    </b>
                    <ol className="list-decimal ml-4" start={5}>
                        <li className="my-2 mx-0">Identity of tank(s).</li>
                        <li className="my-2 mx-0">
                            Position of ship at start of discharge.
                        </li>
                        <li className="my-2 mx-0">
                            Position of ship on completion of discharge.
                        </li>
                        <li className="my-2 mx-0">
                            Ship's speed(s) during discharge.
                        </li>
                        <li className="my-2 mx-0">
                            Method of discharge:
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    through 15 ppm equipment;
                                </li>
                                <li className="my-2 mx-0">
                                    to reception facilities.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            Quantity discharged, in m<sup>3</sup>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>
                        Collection, transfer and disposal of oil residues
                        (sludge)
                    </b>
                    <ol type="1" start={11} className="list-decimal ml-4">
                        <li className="my-2 mx-0 mb-4">
                            Collection of oil residues (sludge).
                            <p className="my-3 mx-0">
                                Quantities of oil residues (sludge) retained on
                                board. The quantity should be recorded weekly{' '}
                                <sup>1</sup>: (This means that the quantity must
                                be recorded once a week even if the voyage lasts
                                more than one week):
                            </p>
                            <ol type="1" className="list-decimal ml-4">
                                <li className="my-1 mx-0">
                                    identity of tank(s)
                                </li>
                                <li className="my-1 mx-0">
                                    <span>capacity of tank(s)</span>
                                    <span className="inline-block py-0 px-3 text-[#808080] border-b border-dashed border-[#808080] mb-0 ml-2 min-w-[150px]">
                                        {/* placeholder m³ */}
                                    </span>
                                    <span>m³</span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span>total quantity of retention</span>
                                    <span className="inline-block py-0 px-3 text-[#808080] border-b border-dashed border-[#808080] mb-0 ml-2 min-w-[150px]">
                                        {/* placeholder m³ */}
                                    </span>
                                    <span>m³</span>
                                </li>
                                <li className="my-1 mx-0">
                                    <div>
                                        <span>
                                            quantity of residue collected by
                                            manual operation
                                        </span>
                                        <span className="inline-block py-0 px-3 text-[#808080] border-b border-dashed border-[#808080] mb-0 ml-2 min-w-[150px]">
                                            {/* placeholder m³ */}
                                        </span>
                                        <span>m³</span>
                                        <p className="my-2 mx-0">
                                            (Operator initiated manual
                                            collections where oil residue
                                            (sludge) si transferred into the oil
                                            residue (sludge) holding tank(s).)
                                        </p>
                                    </div>
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            Methods of transfer or disposal of oil residues
                            (sludge).
                            <p className="my-3 mx-0">
                                State quantity of oil residues transferred or
                                disposed of, the tank(s) emptied and the
                                quantity of contents retained in m³:
                            </p>
                            <ol type="1" className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    to reception facilities (identify port){' '}
                                    <sup>2</sup> ;
                                </li>
                                <li className="my-2 mx-0">
                                    to another (other) tank(s) (indicate tank(s)
                                    and the total content of tank(s))
                                </li>
                                <li className="my-2 mx-0">
                                    incinerated (indicate total time of
                                    operation);
                                </li>
                                <li className="my-2 mx-0">
                                    other method (state which).
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>
            </ol>
            <div className="py-0 px-5 mt-32 mb-16">
                <div
                    className=" border-[#2D3748] border-[0.5px]  mt-0 mb-3 mx-0"
                    style={{ width: 224 }}
                />
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">1</sup>
                    <span className="text-sm">
                        Only those tanks listed in item 3.1 of Forms A and B of
                        the supplement to the IOPP Certificate used for oil
                        residues (sludge).
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">2</sup>
                    <span className="text-sm">
                        The ship's master should obtain from the operator of the
                        reception facilities, which includes barges and tank
                        trucks, a receipt or certificate detailing the quantity
                        of tank washings, dirty ballast, residues or oily
                        mixtures transferred, together with the time and date of
                        the transfer. This receipt or certificate, if attached
                        to the Oil Record Book Part I, may aid the master of the
                        ship in proving that his ship was not involved in an
                        alleged pollution incident. The receipt or certificate
                        should be kept together with the Oil Record Book Part I.
                    </span>
                </div>
            </div>
            <ol className="newpage list-alpha ml-4" type="A" start={4}>
                <li className="mb-8">
                    <b>
                        Non-automatic starting of discharge overboard, transfer
                        or disposal otherwise of bilge water which has
                        accumulated in machinery spaces
                    </b>
                    <ol start={13} className="list-decimal ml-4">
                        <li className="my-2 mx-0">
                            Quantity discharged or disposed of, in m³.
                            <sup style={{ fontSize: 12 }}>3</sup>
                        </li>
                        <li className="my-2 mx-0">
                            Time of discharge, transfer or disposal (start and
                            stop).
                        </li>
                        <li className="my-2 mx-0">
                            Method of discharge, transfer or disposal:
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    through 15 ppm equipment (state position at
                                    start and end);
                                </li>
                                <li className="my-2 mx-0">
                                    to reception facilities (identify port){' '}
                                    <sup style={{ fontSize: 12 }}>4</sup> ;
                                </li>
                                <li className="my-2 mx-0">
                                    to slop tank or holding tank (indicate
                                    tank(s); state the total quantity retained
                                    in tank(s), in m³).
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>
                        Automatic starting of discharge overboard, transfer or
                        disposal otherwise of bilge water which has accumulated
                        in machinery spaces
                    </b>
                    <ol start={16} className="list-decimal ml-4">
                        <li className="my-2 mx-0">
                            Time and position of ship at which the system has
                            been put into automatic mode of operation for
                            discharge overboard, through 15 ppm equipment.
                        </li>
                        <li className="my-2 mx-0">
                            Time when the system has been put into automatic
                            mode of operation for transfer of bilge water to
                            holding tank (identify tank).
                        </li>
                        <li className="my-2 mx-0">
                            Time when the system has been put into manual
                            operation.
                        </li>
                    </ol>
                </li>
                <li className="mb-8">
                    <b>Condition of the oil filtering equipment</b>
                    <ol start={19} className="list-decimal ml-4">
                        <li className="my-2 mx-0">
                            Time of system failure.{' '}
                            <sup style={{ fontSize: 12 }}>5</sup>
                        </li>
                    </ol>
                </li>
            </ol>
            <div className="py-0 px-5 my-8">
                <div
                    className=" border-[#2D3748] border-[0.5px]  mt-0 mb-3 mx-0"
                    style={{ width: 224 }}
                />
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">3</sup>
                    <span className="text-sm">
                        In case of discharge or disposal of bilge water from
                        holding tank(s), state identity and capacity of holding
                        tank(s) and quantity retained in holding tank.
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">4</sup>
                    <span className="text-sm">
                        The ship's master should obtain from the operator of the
                        reception facilities, which includes barges and tank
                        trucks, a receipt or certificate detailing the quantity
                        of tank washings, dirty ballast, residues or oily
                        mixtures transferred, together with the time and date of
                        the transfer. This receipt or certificate, if attached
                        to the Oil Record Book Part I, may aid the master of the
                        ship in proving that his ship was not involved in an
                        alleged pollution incident. The receipt or certificate
                        should be kept together with the Oil Record Book Part I.
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">5</sup>
                    <span className="text-sm">
                        The condition of the oil filtering equipment covers also
                        the alarm and automatic stopping devices, if applicable
                    </span>
                </div>
            </div>
            <div className="newpage">
                <ol className="list-decimal ml-8" start={20}>
                    <li className="my-2 mx-0">
                        Time when system has been made operational.
                    </li>
                    <li className="my-2 mx-0">Reasons for failure.</li>
                </ol>
                <ol className=" list-alpha ml-4" type="A" start={7}>
                    <li className="mb-8 my-12">
                        <b>Accidental or other exceptional discharges of oil</b>
                        <ol className="list-decimal ml-4" start={22}>
                            <li className="my-2 mx-0">Time of occurrence.</li>
                            <li className="my-2 mx-0">
                                Place or position of ship at time of occurrence.
                            </li>
                            <li className="my-2 mx-0">
                                Approximate quantity and type of oil.
                            </li>
                            <li className="my-2 mx-0">
                                Circumstances of discharge or escape, the
                                reasons therefore and general remarks.
                            </li>
                        </ol>
                    </li>
                    <li className="mb-8">
                        <b>Bunkering of fuel or bulk lubricating oil</b>
                        <ol type="1" start={26} className="list-decimal ml-4">
                            <li className="my-2 mx-0">
                                Bunkering:
                                <ol className="list-decimal ml-4">
                                    <li className="my-2 mx-0">
                                        Place of bunkering.
                                    </li>
                                    <li className="my-2 mx-0">
                                        Time of bunkering.
                                    </li>
                                    <li className="my-2 mx-0">
                                        Type and quantity of fuel oil and
                                        identity of tank(s) (state quantity
                                        added, in tonnes and total content of
                                        tank(s)).
                                    </li>
                                    <li className="my-2 mx-0">
                                        Type and quantity of lubricating oil and
                                        identity of tank(s) (state quantity
                                        added, in tonnes and total content of
                                        tank(s)).
                                    </li>
                                </ol>
                            </li>
                        </ol>
                    </li>
                </ol>
            </div>
        </div>
    );
}

function ORB1CoverPage(props: ORB1CoverPageProps) {
    const { vesselName, imoNumber, dateRange, distinctiveNumberOrLetters } =
        props;
    // returns the cover page for the marpol oil record book 1
    return (
        <div className="flex flex-col justify-between py-4 px-8 mb-2">
            <div className="px-4">
                <div
                    style={{ marginTop: '10rem' }}
                    className="font-medium text-center text-xl  mb-24"
                >
                    <h3 className="uppercase">Oil Record Book</h3>
                    <h4>PART 1 - Machinery space operations</h4>
                    <span>(AllShips)</span>
                </div>

                {/* ship descriptor */}

                <div className="flex justify-center mt-8 mb-60">
                    <div className="w-2/3 mt-2.5">
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Name of ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {vesselName}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">
                                Distinctive number or letters:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {distinctiveNumberOrLetters}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">IMO Number:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {imoNumber}
                            </p>
                        </div>

                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">
                                Gross tonnage:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {props?.tonnage?.grossTonnage || ''}
                            </p>
                        </div>
                        <div
                            className="flex justify-between items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <div
                                style={{ width: '54%' }}
                                className="flex items-center  mb-2"
                            >
                                <p className="mr-2 min-w-fit">Period from :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.startDate &&
                                        moment(dateRange?.startDate).format(
                                            'DD-MM-yyyy'
                                        )}{' '}
                                </p>
                            </div>
                            <div
                                style={{ width: '45%' }}
                                className="flex items-center  mb-2"
                            >
                                <p className="mr-2 min-w-fit">to :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.endDate &&
                                        moment(dateRange?.endDate).format(
                                            'DD-MM-yyyy'
                                        )}
                                </p>
                            </div>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Working language of the ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                English
                            </p>
                        </div>
                    </div>
                </div>

                <div className="border p-1 mt-8 mb-4 border-[#2D3748]">
                    Note: Oil Record Book Part 1 shall be provided to every oil
                    tanker of 150 gross tonnage and above and every ship of 400
                    gross tonnage and above, other than oil tanker, to record
                    relevant machinery space operations. For oil tankers, Oil
                    Record Book Part II shall also be provided to record
                    relevant cargo/ballast operations.
                </div>
            </div>
            <IntroductionPage />
            <div className="my-8 mx-0">
                <ItemsTobeRecorded />
            </div>
        </div>
    );
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
    // const authStore = authSelectors._getStore(store);
    const inspectorStore = inspectorSelectors._getStore(store);
    // const vesselData = authSelectors.getVesselInfo(authStore);
 	const vesselStore = vesselSelectors._getStore(store);
	const vesselInfo = vesselSelectors.getVesselById(
		ownProps.vesselId,
		vesselSelectors.getVessels(vesselStore)
	);
    const dateRange = inspectorSelectors.getDateRange(inspectorStore);

    return {
        vesselName: vesselInfo.vesselName,
        imoNumber: vesselInfo.imoNumber,
        tonnage: vesselInfo.tonnage,
        distinctiveNumberOrLetters: vesselInfo?.distinctiveNumberOrLetters,
        dateRange,
    };
}
const reduxConnector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ORB1CoverPage);
